import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  OptionalAccessories,
  WhyChooseLiftile,
  TechnicalSpecificationTable,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
  ComparisonTable,
  IndustriesWeServe,
  QualitySafetyCommitment,
  OurCommitment,
} from "../components/product";
import {
  ArrowLeftRight,
  ArrowUp,
  Award,
  BadgeCheck,
  Building,
  Cable,
  ClipboardCheck,
  Cog,
  Flag,
  Grid3x3,
  Headphones,
  Layers,
  ListChecks,
  Package,
  Radio,
  ShieldCheck,
  Siren,
  Sparkles,
  ToggleRight,
  Wrench,
  Zap,
  ZapOff,
} from "lucide-react";

/* =====================================================================
   MULTI FUNCTIONAL TOWER HOIST (LMTB Series) — dedicated product page.
   Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/multi-functional-tower-hoist-site-1.jpeg";

const HERO_SPECS = [
  { Icon: ArrowUp, value: "Up to 125 m", label: "Maximum Height" },
  { Icon: Package, value: "Up to 1500 kg", label: "Gross Load Capacity" },
  { Icon: Zap, value: "30–35 m/min", label: "Lifting Speed" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Heavy Duty Construction",
  "Advanced Safety Features",
  "Premium Branded Components",
  "High Performance",
  "Made in India",
];

const GALLERY = [
  {
    src: "/lmtb-new-1.png",
    caption: "LMTB Series on a major infrastructure project — mast attached to a concrete dam wall",
  },
  {
    src: "/lmtb-new-2.png",
    caption: "LIFTILE-branded LMTB winch base on a live multi-storey construction site",
  },
  {
    src: "/lmtb-new-3.png",
    caption: "LMTB Series in operation — orange platform ascending a modern multi-storey building",
  },
  {
    src: "/lmtb-new-4.png",
    caption: "LMTB Series base with orange lifting platform — LIFTILE-branded modular mast sections",
  },
  {
    src: "/lmtb-gallery-5-new.png",
    caption: "LMTB Series on a high-rise construction project — LIFTILE 'Rise With Confidence' banner in view",
  },
  {
    src: "/lmtb-gallery-6-new.png",
    caption: "Rectangular tilting bucket in operation — LMTB Series on a night pour at a multi-storey building",
  },
];

const QUICK_SPECS = [
  { label: "Rated Gross Load", value: "1000–1500 kg" },
  { label: "Maximum Height", value: "Up to 125 m" },
  { label: "Lifting Speed", value: "30–35 m/min" },
  { label: "Brake Motor", value: "10 HP – 12.5 HP" },
];

const MODELS = {
  LMTB1000: {
    name: "LMTB1000",
    tagline: "Versatile 1000 kg model for high-rise construction",
    rows: [
      { label: "Rated Gross Load", value: "1000 kg" },
      { label: "Recommended Payload", value: "800 kg" },
      { label: "Wire Rope", value: "12 mm (6×36)" },
      { label: "Electric Motor", value: "10 HP (7.5 kW Brake Motor)" },
      { label: "Lifting Speed", value: "30–35 m/min" },
      { label: "Maximum Height", value: "125 m" },
      { label: "Mast Section", value: "500 × 400 × 1500 mm" },
      { label: "Rectangle Bucket", value: "1250 × 800 × 610 mm" },
    ],
  },
  LMTB1500: {
    name: "LMTB1500",
    tagline: "Heavy-duty 1500 kg flagship for demanding projects",
    rows: [
      { label: "Rated Gross Load", value: "1500 kg" },
      { label: "Recommended Payload", value: "1200 kg" },
      { label: "Wire Rope", value: "12 mm (6×36)" },
      { label: "Electric Motor", value: "12.5 HP (9.3 kW Brake Motor)" },
      { label: "Lifting Speed", value: "30–35 m/min" },
      { label: "Maximum Height", value: "125 m" },
      { label: "Mast Section", value: "500 × 400 × 1500 mm" },
      { label: "Rectangle Bucket", value: "1400 × 1000 × 500 mm" },
    ],
  },
};

const FEATURES = [
  {
    Icon: ZapOff,
    title: "Advanced Limit Switch Protection",
    desc: "Automatically cuts power at the top and bottom travel limits to protect the machine and improve operator safety.",
  },
  {
    Icon: Cog,
    title: "Bonfiglioli Brake Motor",
    desc: "Premium brake motor ensures safe stopping, precise positioning and reliable operation during power failures.",
  },
  {
    Icon: Sparkles,
    title: "Premium Powder Coating",
    desc: "7-Tank pre-treatment with durable powder coating provides excellent corrosion resistance and long-lasting appearance.",
  },
  {
    Icon: Radio,
    title: "Wired Remote Control",
    desc: "Operate the hoist safely from a convenient location with responsive and precise controls.",
  },
  {
    Icon: Siren,
    title: "Emergency Stop System",
    desc: "Dedicated emergency stop button instantly halts machine operation during unsafe conditions.",
  },
  {
    Icon: BadgeCheck,
    title: "ABB Electrical Components",
    desc: "Premium ABB electrical accessories ensure dependable control, enhanced safety and long-term reliability.",
  },
  {
    Icon: Award,
    title: "Premium Branded Components",
    desc: "Manufactured using trusted brands including Bonfiglioli, ABB, Schneider Electric, Polycab, Usha Martin, FAG Bearings and Elecon.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every machine undergoes testing, inspection and trial operation before dispatch to ensure dependable performance.",
  },
];

const ACCESSORIES = [
  {
    Icon: Package,
    image: "/lmtb-tilting-bucket-new.png",
    title: "Concrete Bucket (Tilting Type)",
    desc: "300 Litres / 0.3 Cubic Meter tilting-type bucket for concrete pours and heavy material transport.",
  },
  {
    Icon: Package,
    image: "/lmtb-rectangle-bucket-new.png",
    title: "Rectangle Material Bucket",
    desc: "Heavy-duty material lifting bucket for bricks, AAC blocks and general construction materials.",
  },
  {
    Icon: Cable,
    image: "/lmtb-rebar-carrier-new.png",
    title: "Rebar Carrier",
    desc: "Available in 7 metre and 9 metre lengths. Maximum capacity 500 kg. Suitable for both vertical and horizontal transportation.",
  },
  {
    Icon: ListChecks,
    title: "Floor Selection System",
    desc: "Optional automatic floor selection for improved operational efficiency across multi-storey lifts.",
  },
  {
    Icon: Wrench,
    image: "/lmtb-manual-erection-crane.png",
    title: "Manual Erection Crane",
    desc: "Assists with mast installation and tower extension during erection on site.",
  },
  {
    Icon: Grid3x3,
    image: "/lmtb-wall-tie-new.png",
    title: "Scaffolding / Wall Tie System",
    desc: "Provides additional structural stability for higher lifting applications tied to the building facade.",
  },
  {
    Icon: ArrowLeftRight,
    title: "Interchangeable Bucket System",
    desc: "Quickly switch between different lifting attachments based on project requirements.",
  },
  {
    Icon: Layers,
    image: "/lmtb-additional-mast.png",
    title: "Additional Mast Sections",
    desc: "500 × 400 × 1500 mm heavy-duty mast sections for increased lifting height configurations.",
  },
];

const TRUST_CARDS = [
  {
    Icon: ToggleRight,
    title: "Multiple Lifting Attachments",
    desc: "Supports different bucket and carrier configurations for multiple applications.",
  },
  {
    Icon: Building,
    title: "High-Rise Ready",
    desc: "Designed for lifting heights up to 125 metres.",
  },
  {
    Icon: Cog,
    title: "Premium Components",
    desc: "Trusted brands ensure dependable performance and long service life.",
  },
  {
    Icon: ShieldCheck,
    title: "Advanced Safety",
    desc: "Brake motor, emergency stop and limit switch protection enhance operational safety.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected",
    desc: "Every machine is personally inspected and trial tested before dispatch.",
  },
  {
    Icon: Headphones,
    title: "Nationwide Support",
    desc: "Installation guidance, technical support and after-sales service across India.",
  },
];

const TECH_SPECS = [
  {
    parameter: "Rated Gross Load",
    values: ["1000 kg", "1500 kg"],
  },
  {
    parameter: "Recommended Payload",
    values: ["800 kg", "1200 kg"],
  },
  {
    parameter: "Wire Rope",
    values: ["12 mm (6×36)", "12 mm (6×36)"],
  },
  {
    parameter: "Electric Motor",
    values: ["10 HP (7.5 kW Brake Motor)", "12.5 HP (9.3 kW Brake Motor)"],
  },
  {
    parameter: "Lifting Speed",
    values: ["30–35 m/min", "30–35 m/min"],
  },
  {
    parameter: "Maximum Height",
    values: ["125 m", "125 m"],
  },
  {
    parameter: "Mast Section",
    values: ["500 × 400 × 1500 mm", "500 × 400 × 1500 mm"],
  },
  {
    parameter: "Rectangle Bucket",
    values: ["1250 × 800 × 610 mm", "1400 × 1000 × 500 mm"],
  },
];

const FAQS = [
  {
    q: "What is the maximum lifting height?",
    a: "The LMTB Series supports lifting heights up to 125 metres depending on project requirements.",
  },
  {
    q: 'What makes the LMTB Series "Multi Functional"?',
    a: "The LMTB Series supports interchangeable lifting attachments including rectangle buckets, concrete buckets, rebar carriers and optional floor selection systems, making one machine suitable for multiple lifting applications.",
  },
  {
    q: "Can the machine lift concrete?",
    a: "Yes.\n\nWith the optional tilting concrete bucket, the LMTB Series can safely transport concrete vertically on construction sites.",
  },
  {
    q: "What safety features are included?",
    a: "The LMTB Series includes brake motor technology, upper and lower limit switches, emergency stop, premium electrical components and multiple safety protection systems.",
  },
  {
    q: "Do you provide installation and after-sales support?",
    a: "Yes.\n\nLIFTILE provides installation guidance, technical assistance, spare parts support and after-sales service across India.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "conventional-tower-hoist",
    name: "Conventional Tower Hoist",
    desc: "Reliable wire-rope tower hoist (LTC Series) for dependable material lifting up to 65 m on residential and industrial projects.",
    image: "/product-conventional-hoist.jpg",
  },
  {
    slug: "passenger-cum-material-hoist",
    name: "Passenger Cum Material Hoist",
    desc: "Twin-cage hoist for safe transport of personnel and materials up to 150 m on high-rise construction sites.",
    image: "/product-passenger-hoist.jpg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact mini jib crane for slab-level material movement — ideal for tight sites and finishing phases.",
    image: "/product-monkey-lift.jpg",
  },
];

const TRUST_BADGES_CTA = [
  "Up to 125 m Height",
  "Premium Branded Components",
  "Owner Inspected Before Dispatch",
  "Nationwide Technical Support",
];

const COMPARISON_ROWS = [
  { feature: "Material Lifting", left: true, right: true },
  { feature: "Rectangle Bucket", left: true, right: true },
  { feature: "Concrete Bucket (Tilting)", left: "Optional", right: true },
  { feature: "Rebar Carrier", left: false, right: true },
  { feature: "Interchangeable Attachments", left: false, right: true },
  { feature: "Floor Selection System", left: false, right: "Optional" },
  { feature: "Manual Erection Crane Compatibility", left: false, right: true },
  { feature: "Versatility", left: "Standard", right: "Excellent" },
  { feature: "Maximum Height", left: "65 m", right: "125 m" },
  { feature: "Overall Productivity", left: "High", right: "Very High" },
];

const INDUSTRIES = [
  {
    Icon: Building,
    title: "Residential Buildings",
    desc: "Reliable material lifting for apartment complexes, villas and residential towers.",
  },
  {
    Icon: Building,
    title: "Commercial Buildings",
    desc: "Efficient vertical transport for offices, malls and mixed-use developments.",
  },
  {
    Icon: Cog,
    title: "Industrial Plants",
    desc: "Dependable lifting for plants, factories and heavy industrial installations.",
  },
  {
    Icon: ShieldCheck,
    title: "Hospitals",
    desc: "Safe material transport for hospital campuses and healthcare infrastructure.",
  },
  {
    Icon: BadgeCheck,
    title: "Hotels",
    desc: "Consistent lifting performance for hotel towers and hospitality projects.",
  },
  {
    Icon: Award,
    title: "Educational Institutions",
    desc: "Trusted lifting solutions for schools, universities and institutional campuses.",
  },
  {
    Icon: Layers,
    title: "Infrastructure Projects",
    desc: "Proven performance on metros, treatment plants and public infrastructure.",
  },
  {
    Icon: Package,
    title: "Warehouse Construction",
    desc: "Fast material movement for large-format warehouses and distribution centres.",
  },
  {
    Icon: ArrowUp,
    title: "High-Rise Buildings",
    desc: "Vertical lifting up to 125 metres for tall towers and mixed-use skyscrapers.",
  },
];

const QUALITY_CARDS = [
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every machine is personally inspected and signed off by the owner before it leaves the factory.",
  },
  {
    Icon: ShieldCheck,
    title: "Trial Run Tested",
    desc: "Full-load trial runs on every machine verify performance, brake response and safety systems.",
  },
  {
    Icon: Award,
    title: "Premium Branded Components",
    desc: "Bonfiglioli, ABB, Schneider Electric, Usha Martin, FAG, Elecon and Polycab — trusted throughout.",
  },
  {
    Icon: Layers,
    title: "Heavy Duty Fabrication",
    desc: "Precision-welded steel structures built on decades of tower-hoist engineering experience.",
  },
  {
    Icon: Sparkles,
    title: "Premium Powder Coating",
    desc: "7-tank surface treatment with durable powder coating for site-grade corrosion resistance.",
  },
  {
    Icon: Headphones,
    title: "Nationwide Technical Support",
    desc: "Installation, spares, service and technical assistance across every project geography in India.",
  },
];

const COMMITMENT_PARAGRAPHS = [
  "At LIFTILE, we believe construction machinery should deliver dependable performance, maximum safety and long-term value.",
  "Every LMTB Series machine is engineered with premium components, thoroughly inspected before dispatch and supported by our technical team to help your projects move forward with confidence.",
];

const COMMITMENT_BADGES = [
  "Premium Components",
  "Owner Inspected",
  "Made in India",
  "Nationwide Support",
];

/* ---------- Blueprint SVG for Section 8 (Download Brochure) ---------- */
const LMTB_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    <rect x="180" y="20" width="40" height="340" strokeDasharray="4 4" />
    <line x1="180" y1="70" x2="220" y2="70" />
    <line x1="180" y1="120" x2="220" y2="120" />
    <line x1="180" y1="170" x2="220" y2="170" />
    <line x1="180" y1="220" x2="220" y2="220" />
    <line x1="180" y1="270" x2="220" y2="270" />
    <line x1="180" y1="320" x2="220" y2="320" />
    {[
      [70, 120],
      [120, 170],
      [170, 220],
      [220, 270],
      [270, 320],
    ].map(([a, b]) => (
      <g key={a}>
        <line x1="180" y1={a} x2="220" y2={b} />
        <line x1="220" y1={a} x2="180" y2={b} />
      </g>
    ))}
    <line x1="60" y1="200" x2="180" y2="200" strokeDasharray="6 3" />
    <rect x="55" y="192" width="10" height="16" />
    <rect x="140" y="360" width="120" height="24" />
    <line x1="80" y1="20" x2="80" y2="360" strokeDasharray="2 4" />
    <text
      x="55"
      y="200"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.6"
    >
      125m
    </text>
  </svg>
);

export default function MultiFunctionalTowerHoistPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Multi Functional Tower Hoist" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lmtb-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LMTB Series"
        title={
          <>
            Multi Functional
            <br />
            Tower Hoist
            <br />
            <span className="text-liftile-orange">(Wire Rope)</span>
          </>
        }
        subtitle="The LIFTILE LMTB Series is engineered for safe and efficient vertical transportation of construction materials with premium components, advanced safety systems and exceptional reliability. Designed for residential, commercial and industrial construction projects up to 125 metres."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lmtb-gallery"
        heading={
          <>
            LMTB Series{" "}
            <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at LIFTILE LMTB Series Multi Functional Tower Hoists on live construction sites across India."
        items={GALLERY}
        seriesLabel="LMTB Series"
        imageAlt="LIFTILE LMTB Series Multi Functional Tower Hoist"
      />

      {/* ─── SECTION 3: QUICK SPECS ─── */}
      <QuickSpecsTabs
        testId="lmtb-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="The essentials — with a model selector to switch between LMTB1000 and LMTB1500 configurations."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LMTB1000"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lmtb-features"
        heading={
          <>
            Built for Performance.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Reliability.
            </span>
          </>
        }
        subtitle="The LMTB Series combines advanced engineering with premium branded components and rigorous quality standards to deliver dependable performance on demanding construction sites."
        items={FEATURES}
      />

      {/* ─── SECTION 5: OPTIONAL ACCESSORIES ─── */}
      <OptionalAccessories
        testId="lmtb-accessories"
        heading={
          <>
            Optional <span className="text-liftile-orange">Accessories</span>
          </>
        }
        subtitle="A complete range of accessories to customize the LMTB Series for specific project applications and lifting requirements."
        items={ACCESSORIES}
      />

      {/* ─── SECTION 6: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lmtb-why-choose"
        image="/lmtb-new-4.png"
        imageAlt="LIFTILE LMTB Series Multi Functional Tower Hoist in operation"
        imageCaption="LMTB Series with rebar carrier delivering reliable material lifting on a live construction project."
        floatingBadge={{ top: "Up to", bottom: "125 metres" }}
        eyebrow="Why LMTB Series"
        heading={
          <>
            Built for Modern <br className="hidden sm:block" />
            <span className="text-liftile-orange">Construction Sites</span>
          </>
        }
        subtitle="The LMTB Series combines advanced engineering, premium branded components and versatile lifting capabilities to maximize productivity and safety on every project."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 7: TECHNICAL SPECIFICATIONS ─── */}
      <TechnicalSpecificationTable
        testId="lmtb-tech-specs"
        heading={
          <>
            Technical{" "}
            <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="Compare all LMTB Series models and choose the ideal solution for your project requirements."
        columns={["LMTB1000", "LMTB1500"]}
        rows={TECH_SPECS}
      />

      {/* ─── SECTION 8: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lmtb-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete technical catalogue including specifications, optional accessories, drawings and product information."
        blueprintSvg={LMTB_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 9: FAQ ─── */}
      <PremiumFAQ
        testId="lmtb-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders and contractors most often ask us about the LMTB Series."
        items={FAQS}
      />

      {/* ─── SECTION 10: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lmtb-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 11 (was): PRODUCT CTA — moved below Our Commitment ─── */}

      {/* ─── SECTION 12: COMPARISON TABLE ─── */}
      <ComparisonTable
        testId="lmtb-comparison"
        heading={
          <>
            Why Choose the{" "}
            <span className="text-liftile-orange">LMTB Series?</span>
          </>
        }
        subtitle="Designed for modern construction projects where flexibility, productivity and safety are essential."
        leftColumnHeader="Conventional Tower Hoist"
        rightColumnHeader="LMTB Series"
        rows={COMPARISON_ROWS}
        background="cream-light"
      />

      {/* ─── SECTION 13: INDUSTRIES ─── */}
      <IndustriesWeServe
        testId="lmtb-industries"
        heading={
          <>
            Trusted Across Every{" "}
            <span className="text-liftile-orange">
              Construction Project
            </span>
          </>
        }
        subtitle="The LMTB Series is trusted by builders, contractors and infrastructure companies across a wide range of industries."
        items={INDUSTRIES}
      />

      {/* ─── SECTION 14: QUALITY & SAFETY ─── */}
      <QualitySafetyCommitment
        testId="lmtb-quality"
        heading={
          <>
            Built Around Quality{" "}
            <span className="text-liftile-orange">and Safety</span>
          </>
        }
        subtitle="Every LMTB Series machine is manufactured using premium materials, trusted branded components and strict quality inspections before dispatch."
        items={QUALITY_CARDS}
      />

      {/* ─── SECTION 15: OUR COMMITMENT ─── */}
      <OurCommitment
        testId="lmtb-commitment"
        heading={
          <>
            Built for Long-Term{" "}
            <span className="text-liftile-orange">Performance</span>
          </>
        }
        paragraphs={COMMITMENT_PARAGRAPHS}
        badges={COMMITMENT_BADGES}
      />

      {/* ─── PRODUCT CTA (relocated below Our Commitment, immediately before global Footer) ─── */}
      <ProductFinalCTA
        testId="lmtb-product-cta"
        backgroundImage="/multi-functional-tower-hoist-site-1.jpeg"
        backgroundAlt="LIFTILE LMTB Series on a live construction site"
        overlayGradient="bg-gradient-to-b from-liftile-black/95 via-liftile-black/88 to-liftile-black/98"
        eyebrow="Let's Build Together"
        heading={
          <>
            Ready to Build{" "}
            <span className="text-liftile-orange">Faster?</span>
          </>
        }
        subtitle="Discover how the LMTB Series can improve productivity, safety and efficiency on your next construction project."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{
          label: "Talk to Our Engineering Team",
          href: "/contact",
        }}
        trustBadges={TRUST_BADGES_CTA}
      />

      <Footer />
    </>
  );
}
